import React, { useEffect, useState } from 'react';
import AppShell from '@/components/layout/AppShell';
import { adminNav } from '@/lib/navConfig';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import KPICard from '@/components/common/KPICard';
import { Wallet, ShoppingBag, Download } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import api, { fmtIDR, currentMonth } from '@/lib/api';
import { toast } from 'sonner';

const MONTH_ID = ['Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni', 'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'];
function monthLabel(m) { try { const [y, mm] = m.split('-'); return `${MONTH_ID[Number(mm) - 1]} ${y}`; } catch { return m; } }

export default function AdminReports() {
  const [month, setMonth] = useState(currentMonth());
  const [summary, setSummary] = useState(null);
  const [best, setBest] = useState([]);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const load = async () => {
      const [s, b] = await Promise.all([
        api.get(`/reports/sales-summary?month=${month}`),
        api.get(`/reports/best-selling?month=${month}&limit=10`),
      ]);
      setSummary(s.data);
      setBest(b.data);
    };
    load();
  }, [month]);

  const downloadReport = async (ppnFilter = 'all') => {
    setDownloading(true);
    try {
      const resp = await api.get(`/reports/monthly-report.pdf?month=${month}&ppn_filter=${ppnFilter}`, { responseType: 'blob' });
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const suffix = ppnFilter === 'include' ? 'IncPPN' : ppnFilter === 'exclude' ? 'ExcPPN' : 'Global';
      a.download = `LaporanPenjualan-${month}-${suffix}-Admin.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Laporan ${suffix} ${monthLabel(month)} diunduh`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal mengunduh laporan');
    } finally { setDownloading(false); }
  };

  return (
    <AppShell nav={adminNav} title="Laporan Penjualan">
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row gap-3 sm:items-end sm:justify-between">
          <div className="max-w-xs w-full sm:w-auto">
            <Label>Bulan</Label>
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="reports-month-admin" />
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button onClick={() => downloadReport('all')} disabled={downloading} data-testid="download-monthly-report-admin">
              <Download className="h-4 w-4 mr-2" /> Laporan Global
            </Button>
            <Button onClick={() => downloadReport('include')} disabled={downloading} variant="outline" className="border-violet-300 text-violet-700 hover:bg-violet-50" data-testid="download-monthly-report-admin-inc">
              <Download className="h-4 w-4 mr-2" /> Inc PPN 11%
            </Button>
            <Button onClick={() => downloadReport('exclude')} disabled={downloading} variant="outline" className="border-sky-300 text-sky-700 hover:bg-sky-50" data-testid="download-monthly-report-admin-exc">
              <Download className="h-4 w-4 mr-2" /> Non-PPN
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <KPICard label="Omset" value={fmtIDR(summary?.revenue || 0)} icon={Wallet} tone="info" />
          <KPICard label="Transaksi" value={(summary?.transactions || 0).toLocaleString('id-ID')} icon={ShoppingBag} />
        </div>
        <div className="text-xs text-muted-foreground">Catatan: HPP dan laba disembunyikan untuk role Admin.</div>

        <Card>
          <CardHeader><CardTitle>Barang Terlaris</CardTitle></CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader><TableRow><TableHead>#</TableHead><TableHead>SKU</TableHead><TableHead>Nama</TableHead><TableHead className="text-right">Qty</TableHead><TableHead className="text-right">Omset</TableHead></TableRow></TableHeader>
              <TableBody>
                {best.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-8 text-sm text-muted-foreground">Belum ada data</TableCell></TableRow>
                ) : best.map((b, i) => (
                  <TableRow key={b.product_id}>
                    <TableCell>{i + 1}</TableCell>
                    <TableCell className="font-mono text-xs">{b.sku}</TableCell>
                    <TableCell>{b.name}</TableCell>
                    <TableCell className="text-right tabular-nums">{b.quantity}</TableCell>
                    <TableCell className="text-right tabular-nums">{fmtIDR(b.revenue)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </AppShell>
  );
}
